import { useContext, useEffect, useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
  Badge,
  Box,
  ButtonBase,
  Collapse,
  IconButton,
  ListItem,
  Popover,
  Tooltip,
  styled,
  tooltipClasses
} from '@mui/material';
import KeyboardArrowDownTwoToneIcon from '@mui/icons-material/KeyboardArrowDownTwoTone';
import KeyboardArrowRightTwoToneIcon from '@mui/icons-material/KeyboardArrowRightTwoTone';
import { useTranslation } from 'react-i18next';

import { SidebarContext } from 'src/contexts/SidebarContext';

const PopoverWrapper = styled(Popover)(
  ({ theme }) => `
    .MuiList-root {
      min-width: 230px;
      padding: ${theme.spacing(1)} !important;
    }
  `
);

const TooltipWrapper = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.colors.alpha.black[100],
    color: theme.palette.getContrastText(theme.colors.alpha.black[100]),
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 'bold',
    borderRadius: theme.general.borderRadiusSm,
    boxShadow: '0 .2rem .8rem rgba(7,9,25,.18), 0 .08rem .15rem rgba(7,9,25,.15)'
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.colors.alpha.black[100]
  }
}));

const renderBadge = (value) => value === ''
  ? <Badge color="primary" variant="dot" />
  : value ? <Badge badgeContent={value} color="primary" /> : null;

const expandedButtonSx = (theme, active, depth) => ({
  width: '100%',
  minHeight: depth ? 34 : 38,
  px: 1,
  pl: 1 + depth * 1.25,
  borderRadius: 0.75,
  justifyContent: 'flex-start',
  color: active ? theme.sidebar.menuItemColorActive : theme.sidebar.menuItemColor,
  bgcolor: active ? theme.sidebar.menuItemBgActive : 'transparent',
  textAlign: 'left',
  transition: theme.transitions.create(['background-color', 'color']),
  '&:hover': {
    color: theme.sidebar.menuItemColorActive,
    bgcolor: theme.sidebar.menuItemBgActive
  },
  '& .menu-icon': {
    width: 24,
    height: 24,
    mr: 1,
    flexShrink: 0,
    color: active ? theme.sidebar.menuItemIconColorActive : theme.sidebar.menuItemIconColor,
    '& .MuiSvgIcon-root': { fontSize: depth ? 17 : 20 }
  },
  '&:hover .menu-icon': { color: theme.sidebar.menuItemIconColorActive }
});

const SidebarMenuItem = ({
  children,
  link,
  icon: Icon,
  badge: badgeValue,
  open: openParent,
  active,
  name,
  expanded,
  depth,
  ...rest
}) => {
  const { t } = useTranslation();
  const { closeSidebar } = useContext(SidebarContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [inlineOpen, setInlineOpen] = useState(Boolean(openParent));

  useEffect(() => {
    if (openParent) setInlineOpen(true);
  }, [openParent]);

  const menuIcon = (
    <Box className="menu-icon" display="flex" alignItems="center" justifyContent="center">
      {Icon ? <Icon /> : <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'currentColor' }} />}
    </Box>
  );

  if (expanded) {
    if (children) {
      return (
        <ListItem component="div" disablePadding sx={{ display: 'block', py: 0.15 }} {...rest}>
          <ButtonBase
            aria-expanded={inlineOpen}
            onClick={() => setInlineOpen((current) => !current)}
            sx={(theme) => expandedButtonSx(theme, active, depth)}
          >
            {menuIcon}
            <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: depth ? '0.75rem' : '0.79rem', fontWeight: active ? 750 : 600 }}>
              {t(name)}
            </Box>
            {renderBadge(badgeValue)}
            {inlineOpen
              ? <KeyboardArrowDownTwoToneIcon sx={{ ml: 0.5, fontSize: 18 }} />
              : <KeyboardArrowRightTwoToneIcon sx={{ ml: 0.5, fontSize: 18 }} />}
          </ButtonBase>
          <Collapse in={inlineOpen} timeout="auto" unmountOnExit>
            {children}
          </Collapse>
        </ListItem>
      );
    }

    return (
      <ListItem component="div" disablePadding sx={{ py: 0.15 }} {...rest}>
        <ButtonBase
          component={RouterLink}
          to={link}
          onClick={closeSidebar}
          className={clsx({ 'Mui-active': active })}
          sx={(theme) => expandedButtonSx(theme, active, depth)}
        >
          {menuIcon}
          <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: depth ? '0.75rem' : '0.79rem', fontWeight: active ? 750 : 600 }}>
            {t(name)}
          </Box>
          {renderBadge(badgeValue)}
        </ButtonBase>
      </ListItem>
    );
  }

  if (children) {
    const popoverOpen = Boolean(anchorEl);
    return (
      <ListItem component="div" disablePadding sx={{ justifyContent: 'center', py: 0.15 }} {...rest}>
        <TooltipWrapper disableInteractive title={t(name)} placement="right" arrow>
          <IconButton
            className={clsx({ 'Mui-active': active || popoverOpen })}
            onClick={(event) => setAnchorEl(event.currentTarget)}
            sx={(theme) => ({
              width: 44,
              height: 44,
              color: theme.sidebar.menuItemColor,
              bgcolor: active ? theme.sidebar.menuItemBgActive : theme.sidebar.menuItemBg,
              '&:hover': { bgcolor: theme.sidebar.menuItemBgActive },
              '& .MuiSvgIcon-root': { color: active ? theme.sidebar.menuItemIconColorActive : theme.sidebar.menuItemIconColor }
            })}
          >
            {Icon && <Icon />}
            {renderBadge(badgeValue)}
          </IconButton>
        </TooltipWrapper>
        <PopoverWrapper
          disableScrollLock
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          transformOrigin={{ vertical: 'center', horizontal: 'left' }}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          onClick={() => setAnchorEl(null)}
          open={popoverOpen}
        >
          {children}
        </PopoverWrapper>
      </ListItem>
    );
  }

  return (
    <ListItem component="div" disablePadding sx={{ justifyContent: 'center', py: 0.15 }} {...rest}>
      <TooltipWrapper disableInteractive title={t(name)} placement="right" arrow>
        <IconButton
          component={RouterLink}
          onClick={closeSidebar}
          to={link}
          className={clsx({ 'Mui-active': active })}
          sx={(theme) => ({
            width: 44,
            height: 44,
            color: theme.sidebar.menuItemColor,
            bgcolor: active ? theme.sidebar.menuItemBgActive : theme.sidebar.menuItemBg,
            '&:hover': { bgcolor: theme.sidebar.menuItemBgActive },
            '& .MuiSvgIcon-root': { color: active ? theme.sidebar.menuItemIconColorActive : theme.sidebar.menuItemIconColor }
          })}
        >
          {Icon && <Icon />}
          {renderBadge(badgeValue)}
        </IconButton>
      </TooltipWrapper>
    </ListItem>
  );
};

SidebarMenuItem.propTypes = {
  children: PropTypes.node,
  active: PropTypes.bool,
  link: PropTypes.string,
  icon: PropTypes.elementType,
  badge: PropTypes.string,
  open: PropTypes.bool,
  name: PropTypes.string.isRequired,
  expanded: PropTypes.bool,
  depth: PropTypes.number
};

SidebarMenuItem.defaultProps = {
  open: false,
  active: false,
  expanded: false,
  depth: 0
};

export default SidebarMenuItem;
